import { useEffect, useMemo, useState } from "react";
import { BookOpen, Search, TriangleAlert } from "lucide-react";

import { EmptyPanel, PageHero } from "@/components/interior-page";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { resourceAnchorId, useResourceHighlight } from "@/hooks/useResourceHighlight";
import {
  fetchPublishedResources,
  fetchResourceCategories,
  groupByCategory,
  type ResourceCategory,
  type ResourceContent,
} from "@/lib/resources";

/**
 * Glossary of acquisition terms. Content is admin-managed — see
 * frontend/src/pages/AdminResourceContent.tsx. Only published + active entries
 * are returned by the API. Search filters client-side across term and
 * definition; the list is small enough that a round trip per keystroke would be
 * wasteful, and the same goes for the category filter.
 */

/** How the results are bucketed. A–Z is the default so the page reads the way
 *  a glossary is expected to; Category is the browse-by-topic view. */
type GroupMode = "letter" | "category";

const ALL = "__all__";

export default function GlossaryPage() {
  const [terms, setTerms] = useState<ResourceContent[] | null>(null);
  const [categories, setCategories] = useState<ResourceCategory[]>([]);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>(ALL);
  const [groupMode, setGroupMode] = useState<GroupMode>("letter");

  useEffect(() => {
    fetchPublishedResources("glossary")
      .then(setTerms)
      .catch(() => setFailed(true));
    // A failed category fetch is not fatal: the terms still render, just
    // without the filter, so it deliberately does not set `failed`.
    fetchResourceCategories("glossary")
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  const { highlightedId } = useResourceHighlight(terms !== null);

  // Search + category filter, applied before either grouping.
  const matches = useMemo(() => {
    if (!terms) return [];
    const q = query.trim().toLowerCase();
    return terms.filter((t) => {
      if (activeCategory !== ALL && t.category_id !== activeCategory) return false;
      if (!q) return true;
      return (
        t.title.toLowerCase().includes(q) ||
        (t.body ?? "").toLowerCase().includes(q) ||
        (t.category ?? "").toLowerCase().includes(q)
      );
    });
  }, [terms, query, activeCategory]);

  // Terms bucketed by first letter — the buckets drive the A / B / C headings.
  const letterGroups = useMemo(() => {
    const byLetter = new Map<string, ResourceContent[]>();
    for (const t of [...matches].sort((a, b) => a.title.localeCompare(b.title))) {
      const letter = (t.title[0] ?? "#").toUpperCase();
      const bucket = byLetter.get(letter);
      if (bucket) bucket.push(t);
      else byLetter.set(letter, [t]);
    }
    return [...byLetter.entries()].map(([heading, items]) => ({ heading, items }));
  }, [matches]);

  // Same terms bucketed by category, in the order the admin arranged them.
  const categoryGroups = useMemo(
    () =>
      groupByCategory(
        [...matches].sort((a, b) => a.title.localeCompare(b.title)),
        categories,
      ).map(({ category, items }) => ({
        heading: category?.name ?? "Uncategorised",
        items,
      })),
    [matches, categories],
  );

  const groups = groupMode === "category" ? categoryGroups : letterGroups;

  // Counts drive the chip labels, so an empty category is visibly empty rather
  // than looking like a filter that silently does nothing.
  const countsByCategory = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of terms ?? []) {
      if (!t.category_id) continue;
      counts.set(t.category_id, (counts.get(t.category_id) ?? 0) + 1);
    }
    return counts;
  }, [terms]);

  const chipClass = (active: boolean) =>
    `rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
      active
        ? "border-primary bg-primary/15 text-primary"
        : "border-border bg-card/60 text-muted-foreground hover:border-primary/40 hover:text-foreground"
    }`;

  return (
    <div className="public-stage pb-8">
      <div className="w-full px-4 py-8 sm:px-6 lg:px-8">
        {/* Shared Dark · Gold interior hero — same header as AI Deal
            Matching and the rest of the interior pages. */}
        <div className="mb-8">
          <PageHero
            eyebrow="Resource Library"
            icon={BookOpen}
            title="Deal"
            titleAccent="Glossary"
            description={"The acquisition terms you'll meet on a deal — explained in plain language."}
          />
        </div>

        {terms && terms.length > 0 && (
          <div className="mb-6 space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="relative w-full sm:max-w-md">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search glossary..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-3">
                {categories.length > 0 && (
                  <div className="flex items-center gap-1 rounded-full border border-border bg-card/60 p-0.5">
                    {(
                      [
                        ["letter", "A–Z"],
                        ["category", "Category"],
                      ] as [GroupMode, string][]
                    ).map(([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setGroupMode(mode)}
                        className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                          groupMode === mode
                            ? "bg-primary/15 text-primary"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
                <span className="whitespace-nowrap text-xs text-muted-foreground">
                  {matches.length} {matches.length === 1 ? "term" : "terms"}
                </span>
              </div>
            </div>

            {categories.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setActiveCategory(ALL)}
                  className={chipClass(activeCategory === ALL)}
                >
                  All terms
                  <span className="ml-1.5 opacity-70">{terms.length}</span>
                </button>
                {categories.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setActiveCategory(c.id)}
                    className={chipClass(activeCategory === c.id)}
                    title={c.description ?? undefined}
                  >
                    {c.name}
                    <span className="ml-1.5 opacity-70">{countsByCategory.get(c.id) ?? 0}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {failed ? (
          <EmptyPanel
            icon={TriangleAlert}
            title="Couldn't load the glossary"
            description="Something went wrong fetching the glossary. Please refresh the page to try again."
          />
        ) : terms === null ? (
          <div className="flex justify-center py-12"><Loader /></div>
        ) : groups.length === 0 ? (
          <EmptyPanel
            icon={BookOpen}
            title={terms.length === 0 ? "No terms published yet" : "No matching terms"}
            description={
              terms.length === 0
                ? "Glossary entries will appear here once they're published."
                : "Nothing in the glossary matches that search. Try a shorter phrase, pick a different category, or clear the filters."
            }
          />
        ) : (
          <div className="space-y-8">
            {groups.map(({ heading, items }) => (
              <section key={heading}>
                <div className="mb-3 flex items-center gap-3">
                  <span
                    className={`font-display font-extrabold text-primary ${
                      groupMode === "category" ? "text-lg" : "text-xl"
                    }`}
                  >
                    {heading}
                  </span>
                  <span className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground">{items.length}</span>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map((t) => (
                    <div
                      key={t.id}
                      id={resourceAnchorId(t.id)}
                      className={`flex scroll-mt-24 flex-col rounded-2xl border p-5 transition duration-700 hover:border-primary/40 hover:shadow-sm ${
                        highlightedId === t.id
                          ? "border-primary bg-primary/10 shadow-md ring-2 ring-primary/60"
                          : "border-border bg-card/60"
                      }`}
                    >
                      {/* Redundant inside the category grouping, so it is only
                          shown when the heading isn't already the category. */}
                      {t.category && groupMode !== "category" && (
                        <span className="mb-1 inline-block text-[0.65rem] font-bold uppercase tracking-wide text-primary">
                          {t.category}
                        </span>
                      )}
                      <h3 className="text-base font-bold text-foreground">{t.title}</h3>
                      <p className="mt-1 whitespace-pre-wrap text-sm leading-6 text-muted-foreground">
                        {t.body}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
